import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../Button';
import { formatCurrency } from '../../utils/currency';
import { formatThousands, sanitizeRate } from '../../utils/format';
import { scrollToLeadForm } from '../../utils/scroll';
import { useLoanEstimate, type CalculatorInput } from './useLoanEstimate';
const TERMS = [36, 48, 60, 72, 84];
const INITIAL: CalculatorInput = {
  // Nothing is pre-filled: the page never advertises a price, rate, term, or payment.
  price: '',
  down: '',
  tradeValue: '',
  owed: '',
  apr: '',
  term: 60
};
const MONEY_FIELDS = [{
  key: 'price' as const,
  label: 'calc.price',
  help: 'calc.help.price',
  ph: 'calc.ph.price'
}, {
  key: 'down' as const,
  label: 'calc.down',
  help: 'calc.help.down',
  ph: 'calc.ph.down'
}, {
  key: 'tradeValue' as const,
  label: 'calc.tradeValue',
  help: 'calc.help.tradeValue',
  ph: 'calc.ph.tradeValue'
}, {
  key: 'owed' as const,
  label: 'calc.owed',
  help: 'calc.help.owed',
  ph: 'calc.ph.owed'
}];

/**
 * Self-service estimator written for someone with zero finance background: every
 * field carries a plain-language explanation and any field can be left blank.
 *
 * All numbers come from the visitor. Results are labeled as estimates and are NOT an
 * offer, an approval, or an available rate. Nothing entered here is tracked or sent
 * anywhere — it stays in the browser.
 */
export function PaymentCalculator() {
  const {
    t,
    lang
  } = useLanguage();
  const [values, setValues] = useState<CalculatorInput>(INITIAL);
  const estimate = useLoanEstimate(values);
  const setField = (key: keyof CalculatorInput, value: string | number) => setValues(prev => ({
    ...prev,
    [key]: value
  }));
  const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-muted';
  const helpClass = 'mb-2 text-xs leading-relaxed text-muted';
  return <div className="border border-ink/12 bg-white p-6 sm:p-8" data-id="element-26" data-mp="2ul12ca6z15uc">
      <h3 className="font-display text-xl font-extrabold uppercase tracking-[0.1em] text-ink sm:text-2xl" data-id="element-27" data-mp="2unn7622h978v">
        {t('calc.title')}
      </h3>
      <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted" data-id="element-28" data-mp="310hpnvhdh4t4">{t('calc.sub')}</p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-29" data-mp="2n2kqcrbjakar">
        {MONEY_FIELDS.map(field => <div key={field.key} data-id="element-30" data-mp="2rztdl6bjheiv">
            <label htmlFor={`calc-${field.key}`} className={labelClass} data-id="element-31" data-mp="2rz1idwlutit4">
              {t(field.label)}
            </label>
            <p id={`calc-${field.key}-help`} className={helpClass} data-id="element-32" data-mp="2i2olyqyxcquo">
              {t(field.help)}
            </p>
            <div className="flex items-center border border-ink/20 bg-white focus-within:border-ink" data-id="element-33" data-mp="24bjs65i8mss9">
              <span aria-hidden="true" className="pl-4 text-base text-muted" data-id="element-34" data-mp="28cdif5j5kjob">
                $
              </span>
              <input id={`calc-${field.key}`} type="text" inputMode="numeric" placeholder={t(field.ph)} value={values[field.key]}
          // Formatted live with thousands separators so the number stays readable.
          onChange={e => setField(field.key, formatThousands(e.target.value))} aria-describedby={`calc-${field.key}-help`} className="min-h-[52px] w-full bg-transparent px-2 text-base tabular-nums text-body placeholder:text-muted/60 focus:outline-none" data-id="element-35" data-mp="1ue5euerm2k46" />
            </div>
          </div>)}

        <div data-id="element-36" data-mp="1ymwswgzwb483">
          <label htmlFor="calc-apr" className={labelClass} data-id="element-37" data-mp="1tq6hw82y6ytj">
            {t('calc.apr')}
          </label>
          <p id="calc-apr-help" className={helpClass} data-id="element-38" data-mp="28et3usn2yotq">
            {t('calc.help.apr')}
          </p>
          <div className="flex items-center border border-ink/20 bg-white focus-within:border-ink" data-id="element-39" data-mp="23g9dmg1geqvk">
            <input id="calc-apr" type="text" inputMode="decimal" placeholder={t('calc.ph.apr')} value={values.apr} onChange={e => setField('apr', sanitizeRate(e.target.value))} aria-describedby="calc-apr-help" className="min-h-[52px] w-full bg-transparent px-4 text-base tabular-nums text-body placeholder:text-muted/60 focus:outline-none" data-id="element-40" data-mp="2hf95dwk9jlvy" />
            <span aria-hidden="true" className="pr-4 text-base text-muted" data-id="element-41" data-mp="kjnz88lgc3ir">
              %
            </span>
          </div>
        </div>

        <div data-id="element-42" data-mp="3nytqo7b47888">
          <label htmlFor="calc-term" className={labelClass} data-id="element-43" data-mp="3nz24c1f2ahkh">
            {t('calc.term')}
          </label>
          <p id="calc-term-help" className={helpClass} data-id="element-44" data-mp="3sxwlz01am030">
            {t('calc.help.term')}
          </p>
          <select id="calc-term" value={values.term} onChange={e => setField('term', Number(e.target.value))} aria-describedby="calc-term-help" className="min-h-[52px] w-full appearance-none border border-ink/20 bg-white px-4 text-base text-body focus:border-ink focus:outline-none" data-id="element-45" data-mp="1p8zggrbg6tv">
            {TERMS.map(term => <option key={term} value={term} data-id="element-46" data-mp="31obmzrnrgzzp">
                {term} {t('calc.monthsWord')}
              </option>)}
          </select>
        </div>
      </div>

      <p className="mt-5 text-sm text-body" data-id="element-47" data-mp="2swi0loljwv27">{t('calc.unknown')}</p>

      <div aria-live="polite" className="mt-6 border-t border-camel/40 pt-6" data-id="element-48" data-mp="2ny849ao3bof0">
        {estimate.hasEnoughInput ? <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-49" data-mp="2iyvfulhkm9ab">
            <div data-id="element-50" data-mp="2ix0whbly6p8c">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-camelink" data-id="element-51" data-mp="2e00qt1t0fohr">
                {t('calc.result')}
              </p>
              <p className="mt-1 font-display text-4xl font-extrabold text-ink" data-id="element-52" data-mp="29z8hk8k51jlh">
                {formatCurrency(estimate.monthlyPayment, lang)}
                <span className="ml-1 text-base font-semibold text-muted" data-id="element-53" data-mp="872db3ljjohb">
                  /{t('calc.perMonth')}
                </span>
              </p>
              <p className="mt-2 text-sm text-muted" data-id="element-54" data-mp="39ak8695twbj">
                {t('calc.financed')}: {formatCurrency(estimate.amountFinanced, lang)}
              </p>
            </div>

            <div className={`border-l-2 pl-4 ${estimate.isUpsideDown ? 'border-kred' : 'border-camel'}`} data-id="element-55" data-mp="r3kpbj223zpl">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-camelink" data-id="element-56" data-mp="ddr215679nhz">
                {estimate.isUpsideDown ? t('calc.equityNegative') : t('calc.equityPositive')}
              </p>
              <p className="mt-1 font-display text-2xl font-extrabold text-ink" data-id="element-57" data-mp="161d1q2cb47zk">
                {formatCurrency(Math.abs(estimate.equity), lang)}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-58" data-mp="w1ciegeftrvd">
                {estimate.isUpsideDown ? t('calc.equityNegativeNote') : t('calc.equityPositiveNote')}
              </p>
            </div>
          </div> : <p className="text-sm text-muted" data-id="element-59" data-mp="3guppzqcrhw1q">{t('calc.empty')}</p>}
      </div>

      <Button type="button" variant="outline" onClick={scrollToLeadForm} className="mt-6 sm:px-10" data-id="element-60" data-mp="3ltaq7ps0kfq6">
        {t('calc.cta')}
      </Button>

      <p className="mt-5 text-xs leading-relaxed text-muted" data-id="element-61" data-mp="3993xvhqobiyk">{t('calc.disclaimer')}</p>
    </div>;
}